/* eslint max-lines: ["error", { max: 385 }] -- flag-gated v1/v2 details */
/* eslint max-lines-per-function: ["error", { max: 185 }] -- flag-gated v1/v2 details */
/* eslint max-statements: ["error", { max: 26 }] -- flag-gated v1/v2 details */
/* eslint complexity: ["error", { max: 14 }] -- flag-gated v1/v2 details */
import { useCallback, useEffect, useState } from "react";

import isNil from "lodash/isNil";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import {
  AnalyticsFieldName,
  GroupClassSeriesAnalyticsEvent,
  GroupClassSeriesAnalyticsPageView,
  GroupClassSeriesAnalyticsSources,
} from "@/constants/analytics/Analytics";
import type { TabPath } from "@/constants/Navigation";
import { ModalPath } from "@/constants/Navigation";
import { ExploreTestID, ModalTestID } from "@/constants/TestID";
import { router } from "@/crossPlatform/expo-router";
import useFeatureFlags from "@/hooks/useFeatureFlags";
import type { OnCompletionCallback } from "@/hooks/useGroupClassRegistration";
import useGroupDetailsErrorHandler from "@/hooks/useGroupDetailsErrorHandler";
import { isUserPreTreatment } from "@/hooks/usePatientStatus";
import useScheduleQueryPatientId from "@/hooks/useScheduleQueryPatientId";
import { useAnalytics } from "@/services/Analytics";
import {
  useGetGroupClassesQuery,
  useGetGroupDetailsQuery,
} from "@/store/api/schedule/groupClasses";
import { useGetFullProfileQuery } from "@/store/api/user";
import { useAppSelector } from "@/store/hooks";
import { useAppUserSelector } from "@/store/reducers/appUser";
import { selectGroupClassDetailsData } from "@/store/reducers/modals/groupClassDetailsModal";
import RegisterButton from "@/ui/components/groupClasses/RegisterButton";
import GroupClassSeriesDetails from "@/ui/components/groupClasses/series/details/GroupClassSeriesDetails";
import GroupDetailsV2 from "@/ui/components/groupClasses/series/details/GroupDetailsV2";
import EditGroupEventBottomSheetModal from "@/ui/components/schedule/bottomSheetModals/EditGroupEventBottomSheetModal";
import ModalTitleHeader from "@/ui/composed/modal/ModalTitleHeader";
import FloatingButtonWrapper from "@/ui/core/buttons/FloatingButtonWrapper";
import Spinner from "@/ui/core/Spinner";
import { Box } from "@/ui/layout";
import ModalLayout from "@/ui/layout/ModalLayout";
import {
  shouldHideRegistrationStatus,
  toDetailsProps,
} from "@/utils/groupClass.util";
import { isSamsungAPI35Plus, isWeb } from "@/utils/platform.util";
import { isNotNilNotEmpty } from "@/utils/shared.utils";
import { joinTestIDs } from "@/utils/string.util";

const baseTestID = ExploreTestID.GroupClassSeriesDetails;

// on web, ModalLayout.web otherwise shows the spinner top-aligned flex
const CenteredSpinner = () =>
  isWeb() ? (
    <Box alignItems="center" flex={1} justifyContent="center">
      <Spinner />
    </Box>
  ) : (
    <Spinner />
  );

type GroupClassDetailsDismissPath =
  | TabPath.Explore
  | TabPath.Home
  | typeof ModalPath.GroupClassList;

const useSelectedPatientUniversalStatusCode = (
  patientId?: string,
): undefined | UniversalStatusCode => {
  const { externalId: appUserId, linkedPatients } = useAppUserSelector();
  const { data: fullProfileData } = useGetFullProfileQuery();

  if (!isNotNilNotEmpty(patientId)) return undefined;

  const profile =
    patientId === appUserId
      ? fullProfileData
      : linkedPatients?.find((p) => p.externalId === patientId);

  return profile?.universalStatusCode;
};

const ModalContent = ({
  groupClass,
  isAppUserSupport,
  isSelectedUserPreTreatment,
  onUnregistration,
  patientId,
}: {
  groupClass?: GroupClassSeries;
  isAppUserSupport?: boolean;
  isSelectedUserPreTreatment: boolean;
  onUnregistration: () => void;
  patientId?: string;
}) => {
  const [
    isEditGroupRegistrationModalOpen,
    setIsEditGroupRegistrationModalOpen,
  ] = useState<boolean>(false);
  const openEditEventModal = useCallback(
    () => setIsEditGroupRegistrationModalOpen(true),
    [],
  );
  const closeEditEventModal = useCallback(
    () => setIsEditGroupRegistrationModalOpen(false),
    [],
  );

  if (
    isNil(groupClass) ||
    isNil(groupClass.groupClassExternalId) ||
    isNil(isAppUserSupport) ||
    !isNotNilNotEmpty(patientId)
  ) {
    return <CenteredSpinner />;
  }

  const {
    attendees,
    dateRange,
    description,
    externalId,
    iconName,
    isRecurring,
    isSensitive,
    isUserRegistered,
    nextStartDate,
    recurrenceString,
    timeRange,
    title,
  } = toDetailsProps({
    groupClass,
    isAppUserSupport,
  });
  return (
    <>
      <GroupClassSeriesDetails
        attendees={attendees}
        dateRange={dateRange}
        description={description}
        externalId={externalId}
        iconName={iconName}
        isAppUserSupport={isAppUserSupport}
        isRecurring={isRecurring}
        isSensitive={isSensitive}
        isUserPreTreatment={isSelectedUserPreTreatment}
        isUserRegistered={isUserRegistered}
        nextStartDate={nextStartDate}
        onEditEventPress={openEditEventModal}
        recurrenceString={recurrenceString}
        timeRange={timeRange}
        title={title}
      />
      <EditGroupEventBottomSheetModal
        eventTitle={title}
        groupClassSeriesExternalId={externalId}
        isOpen={isEditGroupRegistrationModalOpen}
        onClose={closeEditEventModal}
        onCompletion={onUnregistration}
        patientExternalId={patientId}
      />
    </>
  );
};

const V2Content = ({
  group,
  groupSectionId,
  isLoading,
  isUserPreTreatment: isSelectedUserPreTreatment,
  onRegistrationError,
  patientId,
  source,
}: {
  group?: GroupDetails;
  groupSectionId?: string;
  isLoading: boolean;
  isUserPreTreatment: boolean;
  onRegistrationError: () => void;
  patientId?: string;
  source: GroupClassSeriesAnalyticsSources;
}) => {
  if (isLoading) return <Spinner />;
  if (isNil(group) || isNil(patientId)) return null;
  return (
    <GroupDetailsV2
      group={group}
      groupSectionId={groupSectionId}
      isUserPreTreatment={isSelectedUserPreTreatment}
      onRegistrationError={onRegistrationError}
      patientId={patientId}
      source={source}
    />
  );
};

const GroupClassDetailsModal = ({
  dismissToPath,
}: {
  dismissToPath: GroupClassDetailsDismissPath;
}) => {
  const insets = useSafeAreaInsets();
  const { track, trackPageView } = useAnalytics();
  const { isGroupClassRedesignEnabled } = useFeatureFlags();
  const groupClassData = useAppSelector(selectGroupClassDetailsData);
  const {
    forWhom,
    groupClassExternalId,
    groupSectionId,
    isAppUserSupport,
    isDisabled,
    isRecommended = false,
    patientId,
    // entry points set this when they open the modal; fall back to the details screen itself
    // for any path that doesn't (matches the v1 register/unregister events fired from here)
    source = GroupClassSeriesAnalyticsSources.Details,
  } = groupClassData || {};

  const universalStatusCode = useSelectedPatientUniversalStatusCode(patientId);
  const isSelectedUserPreTreatment =
    !isNil(universalStatusCode) && isUserPreTreatment(universalStatusCode);

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const patientIdForQuery = useScheduleQueryPatientId(patientId!);
  const { data: queryData } = useGetGroupClassesQuery(
    { patientId: patientIdForQuery },
    // eslint-disable-next-line @typescript-eslint/naming-convention -- RTKQuery option
    { skip: isGroupClassRedesignEnabled },
  );
  const {
    data: groupDetails,
    error: groupDetailsError,
    isError: isGroupDetailsError,
    isLoading: isGroupDetailsLoading,
  } = useGetGroupDetailsQuery(
    {
      groupId: groupClassExternalId ?? "",
      patientExternalId: patientIdForQuery,
    },
    {
      // eslint-disable-next-line @typescript-eslint/naming-convention -- RTKQuery option
      skip: !isGroupClassRedesignEnabled || isNil(groupClassExternalId),
    },
  );
  const groupClass = queryData?.find(
    (gc: GroupClassSeries) => gc.groupClassExternalId === groupClassExternalId,
  );
  const isUserRegistered = Boolean(groupClass?.isUserRegistered);

  useEffect(() => {
    if (isNil(groupClass)) return;
    trackPageView(GroupClassSeriesAnalyticsPageView.Details, {
      [AnalyticsFieldName.IsRecommended]: isRecommended,
      [AnalyticsFieldName.Title]: groupClass.title,
    });
  }, [groupClass, isRecommended, trackPageView]);

  const isSensitive = Boolean(groupClass?.isSensitive);
  const shouldHideRegistration = shouldHideRegistrationStatus({
    isAppUserSupport,
    isSensitive,
  });
  // pre-treatment patients can not register/unregister via the app
  const shouldShowRegistrationStatus =
    !shouldHideRegistration && !isSelectedUserPreTreatment;

  const onRegistrationChange: OnCompletionCallback = useCallback(
    ({ wasRegistered }) => {
      const eventName = wasRegistered
        ? GroupClassSeriesAnalyticsEvent.UnregisterPressed
        : GroupClassSeriesAnalyticsEvent.RegisterPressed;
      const attendeeTypes = isNil(groupClass?.attendeeTypes)
        ? {}
        : {
            // eslint-disable-next-line sonarjs/no-alphabetical-sort -- alphabetical sort is desired here
            attendeeTypes: JSON.stringify([...groupClass.attendeeTypes].sort()),
          };
      track(eventName, {
        ...attendeeTypes,
        [AnalyticsFieldName.Component]: ModalPath.GroupClassDetails,
        [AnalyticsFieldName.GroupClassId]: groupClass?.groupClassExternalId,
        [AnalyticsFieldName.PatientId]: patientId || "",
        [AnalyticsFieldName.Source]: GroupClassSeriesAnalyticsSources.Details,
        [AnalyticsFieldName.Title]: groupClass?.title,
      });
    },
    [groupClass, patientId, track],
  );

  const onDismiss = useCallback(() => {
    router.dismissTo(dismissToPath);
  }, [dismissToPath]);

  useGroupDetailsErrorHandler({
    error: groupDetailsError,
    groupId: groupClassExternalId,
    isError: isGroupDetailsError,
    onError: onDismiss,
    patientId,
  });

  const onUnregistration = useCallback(() => {
    onRegistrationChange({ isRegistered: false, wasRegistered: true });
    onDismiss();
  }, [onRegistrationChange, onDismiss]);

  if (isNil(groupClassData)) {
    if (isWeb()) {
      // on web, a refresh means fresh redux/no saved state, so spinner will never resolve, so just close modal
      onDismiss();
      return null;
    }
    return <CenteredSpinner />;
  }

  return (
    <ModalLayout
      isPaddedHorizontally
      renderFloatingButton={() => {
        if (
          // v2 registers per-section from the sessions list, not via this button
          isGroupClassRedesignEnabled ||
          isUserRegistered ||
          !shouldShowRegistrationStatus ||
          isNil(groupClassExternalId) ||
          isNil(patientId)
        ) {
          return null;
        }
        return (
          <FloatingButtonWrapper
            paddingBottom={isSamsungAPI35Plus() ? insets.bottom : undefined}
          >
            <RegisterButton
              forWhom={forWhom}
              groupClassExternalId={groupClassExternalId}
              isAppUserSupport={isAppUserSupport}
              isOnPressDisabled={isDisabled}
              isRegistered={isUserRegistered}
              isSensitive={isSensitive}
              onRegistrationChange={onRegistrationChange}
              patientExternalId={patientId}
              size="normal"
              testID={joinTestIDs(baseTestID, ExploreTestID.RegisterButton)}
            />
          </FloatingButtonWrapper>
        );
      }}
      renderHeader={() => (
        <ModalTitleHeader
          onDismiss={onDismiss}
          testID={joinTestIDs(baseTestID, ModalTestID.TitleHeader)}
          title="Group Details"
        />
      )}
    >
      {isGroupClassRedesignEnabled ? (
        <V2Content
          group={groupDetails}
          groupSectionId={groupSectionId}
          isLoading={isGroupDetailsLoading}
          isUserPreTreatment={isSelectedUserPreTreatment}
          onRegistrationError={onDismiss}
          patientId={patientId}
          source={source}
        />
      ) : (
        <ModalContent
          groupClass={groupClass}
          isAppUserSupport={isAppUserSupport}
          isSelectedUserPreTreatment={isSelectedUserPreTreatment}
          onUnregistration={onUnregistration}
          patientId={patientId}
        />
      )}
    </ModalLayout>
  );
};

export default GroupClassDetailsModal;
